import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { changePasswordLoggedInUserAction } from '../../../services/request';

export const PasswordCard = () => {
  const [success, setSuccess] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  const { mutate } = useMutation(
    changePasswordLoggedInUserAction
  );

  const validationSchema = Yup.object({
    old_password: Yup.string()
      .required('Podaj obecne hasło')
      .max(128),

    password1: Yup.string()
      .required('Podaj hasło')
      .max(128),

    password2: Yup.string()
      .required('Powtórz hasło')
      .min(8, 'Minimum 8 znaków')
      .oneOf(
        [Yup.ref('password1')],
        'Hasła muszą być identyczne'
      )
  });

  const formik = useFormik({
    initialValues: {
      old_password: '',
      password1: '',
      password2: ''
    },
    validationSchema,
    onSubmit: async values => {
      const { error, payload } =
        await mutate(values);

      if (!error) {
        setSuccess(true);
        setErrorMessage('');
      } else {
        setErrorMessage(getErrorMessage(payload));
      }
    }
  });

  const getErrorMessage = payload => {
    if (payload?.message) {
      return payload.message;
    }
    const firstError = Object.values(payload || {})[0];

    if (Array.isArray(firstError)) {
      return firstError[0];
    }

    return 'Nie udało się zmienić hasła';
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: theme => theme.shadows[2]
      }}
    >
      <CardContent sx={{ p: 5 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          mb={4}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'primary.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LockOutlinedIcon color="primary" />
          </Box>

          <Typography
            variant="h4"
            fontWeight={700}
          >
            Zmiana hasła
          </Typography>
        </Stack>

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
          >
            Hasło zostało zmienione.
          </Alert>
        )}

        {Boolean(errorMessage) && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
          >
            {errorMessage}
          </Alert>
        )}

        <form
          onSubmit={formik.handleSubmit}
        >
          <Stack spacing={3}>
            <TextField
              fullWidth
              type="password"
              name="old_password"
              label="Obecne hasło"
              value={formik.values.old_password}
              onChange={formik.handleChange}
              error={
                formik.touched.old_password &&
                Boolean(
                  formik.errors.old_password
                )
              }
              helperText={
                formik.touched.old_password &&
                formik.errors.old_password
              }
              disabled={success}
            />
            <TextField
              fullWidth
              type="password"
              name="password1"
              label="Nowe hasło"
              value={formik.values.password1}
              onChange={formik.handleChange}
              error={
                formik.touched.password1 &&
                Boolean(
                  formik.errors.password1
                )
              }
              helperText={
                formik.touched.password1 &&
                formik.errors.password1
              }
              disabled={success}
            />

            <TextField
              fullWidth
              type="password"
              name="password2"
              label="Powtórz nowe hasło"
              value={formik.values.password2}
              onChange={formik.handleChange}
              error={
                formik.touched.password2 &&
                Boolean(
                  formik.errors.password2
                )
              }
              helperText={
                formik.touched.password2 &&
                formik.errors.password2
              }
              disabled={success}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={success}
            >
              Zmień hasło
            </Button>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};
